import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { useScopedPage } from "@/shared/hooks/use-scoped-page";
import { MEMBER_ROLES, MEMBER_STATUSES, MEMBERS_PAGE_SIZE } from "./constants";
import { MembersTable } from "./MembersTable";
import { OrganizationsPagedResults } from "./OrganizationsPagedResults";
import { useOrganizationMembers } from "./use-organization-members";
import { InlineNotice, type Notice } from "@/shared/organizations/InlineNotice";
import { MemberDialogs, type MemberDialogTarget } from "./MemberDialogs";
import type { OrganizationMemberRole, OrganizationMemberStatus } from "./types";

interface OrganizationMembersSectionProps {
  readonly organizationId: string;
  /** `org.admins.manage`: change roles, reactivate an ADMIN. */
  readonly canManageAdmins?: boolean;
  /** `org.members.manage`: a STAFF's locations, reactivating a STAFF, ADMIN -> STAFF. */
  readonly canManageMembers?: boolean;
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring";
const LABEL_CLASS = "font-sans text-sm font-medium text-foreground";

/**
 * Members of one organization (role + status filters, pager). It only mounts
 * once the organization itself loaded, so an unknown id never fires a members
 * read. Its own failure stays in this panel and leaves the rest of the page up.
 */
export function OrganizationMembersSection({
  organizationId,
  canManageAdmins = false,
  canManageMembers = false,
}: OrganizationMembersSectionProps) {
  const { t } = useTranslation();
  const [role, setRole] = useState<OrganizationMemberRole | null>(null);
  const [status, setStatus] = useState<OrganizationMemberStatus | null>(null);
  const [page, setPage] = useScopedPage(`${role ?? ""}|${status ?? ""}`);
  const { data, isLoading, isFetching, isError, error, refetch } = useOrganizationMembers(
    organizationId,
    { role, status, page },
  );
  const [target, setTarget] = useState<MemberDialogTarget | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const items = data?.items ?? [];
  const filtered = role !== null || status !== null;

  return (
    <section
      data-testid="organization-members"
      className="space-y-4 rounded-lg border border-border bg-card p-6"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="brand-eyebrow flex items-center gap-2">
          <Users className="h-4 w-4" aria-hidden="true" />
          {t("organizations.members.title")}
        </h2>
        <form
          role="search"
          aria-label={t("organizations.members.filters.label")}
          onSubmit={(event) => event.preventDefault()}
          className="flex flex-wrap items-end gap-3"
        >
          <div className="space-y-1.5">
            <label htmlFor="members-role" className={LABEL_CLASS}>
              {t("organizations.members.filters.role")}
            </label>
            <select
              id="members-role"
              data-testid="members-filter-role"
              value={role ?? ""}
              onChange={(e) => {
                setRole(e.target.value === "" ? null : (e.target.value as OrganizationMemberRole));
              }}
              className={SELECT_CLASS}
            >
              <option value="">{t("organizations.members.filters.roleAll")}</option>
              {MEMBER_ROLES.map((value) => (
                <option key={value} value={value}>
                  {t(`organizations.members.role.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="members-status" className={LABEL_CLASS}>
              {t("organizations.members.filters.status")}
            </label>
            <select
              id="members-status"
              data-testid="members-filter-status"
              value={status ?? ""}
              onChange={(e) => {
                setStatus(
                  e.target.value === "" ? null : (e.target.value as OrganizationMemberStatus),
                );
              }}
              className={SELECT_CLASS}
            >
              <option value="">{t("organizations.members.filters.statusAll")}</option>
              {MEMBER_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`organizations.members.status.${value}`)}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>

      {notice && <InlineNotice notice={notice} testId="members-notice" />}

      <OrganizationsPagedResults
        testIdPrefix="members"
        loadingLabel={t("organizations.members.loading")}
        page={page}
        limit={data?.limit ?? MEMBERS_PAGE_SIZE}
        totalCount={data?.totalCount ?? 0}
        itemCount={items.length}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        error={error}
        onRetry={() => void refetch()}
        onPageChange={setPage}
        emptyBody={t(
          filtered ? "organizations.members.empty.filtered" : "organizations.members.empty.none",
        )}
        pastEndBody={t("organizations.members.empty.pastEnd")}
      >
        <MembersTable
          items={items}
          permissions={{ manageAdmins: canManageAdmins, manageMembers: canManageMembers }}
          onAction={(action, member) => {
            setNotice(null);
            setTarget({ action, member });
          }}
        />
      </OrganizationsPagedResults>
      <MemberDialogs
        organizationId={organizationId}
        target={target}
        canManageMembers={canManageMembers}
        onClose={() => setTarget(null)}
        onNotice={setNotice}
      />
    </section>
  );
}
